import React from 'react'
import styled, { css } from 'styled-components'
import theme from '../styles/theme'

export interface ButtonProps {
  readonly children: React.ReactNode
  readonly inverted?: boolean
  readonly textual?: boolean
  readonly outline?: boolean
  readonly wide?: boolean
  readonly small?: boolean
  readonly primary?: boolean
  readonly onClick?: () => void
  readonly type?: 'button' | 'submit' | 'reset'
  readonly disabled?: boolean
}

export const Button = styled.button.withConfig({
  shouldForwardProp: (prop) => !['primary', 'inverted', 'textual', 'outline', 'wide', 'small'].includes(prop)
})<ButtonProps>`
  display: inline-block;
  position: relative;
  border: none;
  margin: 0;
  padding: 1.8rem 2em;
  appearance: none;
  overflow: hidden;
  color: ${theme.color.text.light};
  text-align: center;
  transition: all 0.2s ease;
  text-decoration: none;
  background: ${theme.color.text.base};
  font-size: 1.4rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  border-radius: 8px;
  box-shadow: 0 4px 8px ${theme.color.shadow.medium}, 
              0 2px 4px ${theme.color.shadow.light};
  transform: translateY(0);
  
  &:active:not([disabled]) {
    transform: translateY(2px);
    box-shadow: 0 2px 4px ${theme.color.shadow.light}, 
                0 1px 2px ${theme.color.shadow.light};
  }

  &::before {
    content: '';
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    width: 0;
    height: 100%;
    opacity: 0.20;
    transition: 0.4s left, 0.4s width 0.2s;
    transform: skew(-45deg);
    transform-origin: 0 0;
    background: currentColor;
  }

  &:not([disabled]) {
    cursor: pointer;

    &:hover,
    &:focus-visible {
      &::before {
        left: calc(100% + 4.2em);
        width: 100%;
        transition: 0.4s width, 0.4s left 0.2s;
      }
    }
  }

  &[disabled] {
    opacity: 0.3;
  }

  ${props =>
    props.wide &&
    css`
      display: block;
      width: 100%;
    `}

  ${props =>
    (props.inverted ?? props.textual) &&
    css`
      color: ${theme.color.text.base};
      background: ${theme.color.text.light};
    `}

  ${props =>
    props.inverted &&
    css`
      &::before {
        opacity: 0.1;
      }
    `}

  ${props =>
    props.primary &&
    css`
      background-color: ${theme.color.accent.primary};
    `}

  ${props =>
    props.textual &&
    css`
      &::before {
        content: none;
      }

      &:hover,
      &:focus-visible {
        text-decoration: underline;
      }
    `}

  ${props =>
    props.outline &&
    css`
      border: 2px solid currentColor;
      background: transparent;
    `}

  ${props =>
    props.small &&
    css`
      padding: 0.5em 1em 0.45em;
      font-size: 1.1rem;
    `}
`

export default Button